import { useEffect, useRef } from "react";
import { SWRResponse } from "swr";
import { showErrorToast } from "../../components/toast.component";
import { AppRouteApi } from "../../config/appRoute";
import {
  ApiResponseContainer,
  ApiSchemaPatientTask,
  PaginatedData,
} from "../../models/api";
import { AxiosAuth } from "../axios";
import { StorageAuth } from "../localStorage";
import { useAppDispatch, useAppSelector } from "../redux";
import { ActionsTaskTable } from "../redux/slices/sliceTaskTable";
import {
  CreateAccessObject,
  useExtendedApiAccess,
} from "./useExtendedApiAccess";

const CacheIds = {
  GetAll: AppRouteApi.PatientTask.Root(),
  Search: `${AppRouteApi.PatientTask.Search("")}`,
  Paginated: AppRouteApi.PatientTask.Paginated({}),
  ById: AppRouteApi.PatientTask.ById(""),
};

function useApiPatientTaskGetAll(
  companyId?: string
): SWRResponse<ApiSchemaPatientTask[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaPatientTask[]>(
    CacheIds.Paginated,
    () =>
      AxiosAuth.get(CacheIds.GetAll, {
        params: {
          CompanyId: companyId ? companyId : StorageAuth.CompanyId,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsTaskTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaPatientTask[]>
        >(CacheIds.GetAll, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsTaskTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return api;
}

function useApiPatientTaskPaginated(): SWRResponse<
  PaginatedData<ApiSchemaPatientTask> | undefined,
  Error
> {
  const { pageNumber, pageSize, searchKey } = useAppSelector(
    (state) => state.taskTable
  );
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<PaginatedData<ApiSchemaPatientTask>>(
    CacheIds.Paginated,
    () =>
      AxiosAuth.get(CacheIds.Paginated, {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SearchKey: searchKey,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsTaskTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<PaginatedData<ApiSchemaPatientTask>>
        >(CacheIds.Paginated, {
          params: {
            PageNumber: pageNumber,
            PageSize: pageSize,
            SearchKey: searchKey,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsTaskTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchKey]);

  return api;
}

function useApiPatientTaskById(
  id: string
): SWRResponse<ApiSchemaPatientTask | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaPatientTask>(CacheIds.ById, () =>
    AxiosAuth.get(AppRouteApi.PatientTask.ById(id))
  );
}

const AccessObject = {
  GetAll: CreateAccessObject<
    ApiSchemaPatientTask[],
    typeof useApiPatientTaskGetAll
  >(CacheIds.GetAll, useApiPatientTaskGetAll),
  ById: CreateAccessObject<
    ApiSchemaPatientTask[],
    typeof useApiPatientTaskById
  >(CacheIds.ById, useApiPatientTaskById),
  Paginated: CreateAccessObject<
    PaginatedData<ApiSchemaPatientTask>,
    typeof useApiPatientTaskPaginated
  >(CacheIds.Paginated, useApiPatientTaskPaginated),
};

async function AddPatientTask(
  task: ApiSchemaPatientTask
): Promise<ApiResponseContainer<ApiSchemaPatientTask>> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaPatientTask>
    >(AppRouteApi.PatientTask.Root(), {
      ...task,
      companyId: StorageAuth.CompanyId,
    });
    if (response.data.isSuccess) {
      const updAction = async (res?: ApiSchemaPatientTask[]) => {
        if (Array.isArray(res) && response.data.data) {
          return [...res, response.data.data];
        }
        return res;
      };
      AccessObject.GetAll.Mutate(updAction);
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return {
    isSuccess: false,
  };
}

async function UpdatePatientTask(
  task: ApiSchemaPatientTask
): Promise<ApiResponseContainer<ApiSchemaPatientTask>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaPatientTask>
    >(AppRouteApi.PatientTask.Root(), {
      ...task,
      companyId: StorageAuth.CompanyId,
    });
    if (response.data.isSuccess) {
      const updAction = async (res?: ApiSchemaPatientTask[]) => {
        if (Array.isArray(res) && response.data.data) {
          return [
            ...res.filter((i) => i.sKey !== response.data.data?.sKey),
            response.data.data,
          ];
        }
        return res;
      };
      AccessObject.GetAll.Mutate(updAction);
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return {
    isSuccess: false,
  };
}

async function DeletePatientTaskMultiple(
  ids: string | string[]
): Promise<boolean> {
  try {
    const response = Array.isArray(ids)
      ? await AxiosAuth.delete<ApiResponseContainer<ApiSchemaPatientTask>>(
          AppRouteApi.PatientTask.DeleteMultiple(),
          { data: ids }
        )
      : await AxiosAuth.delete<ApiResponseContainer<ApiSchemaPatientTask>>(
          AppRouteApi.PatientTask.Delete(ids)
        );
    if (response.data.isSuccess) {
      const updAction = async (res?: ApiSchemaPatientTask[]) => {
        if (Array.isArray(res)) {
          return res.filter((i) => ids.includes(i.sKey ?? ""));
        }
        return res;
      };
      AccessObject.GetAll.Mutate(updAction);
      return true;
    }
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

const setIsDefaultForDHHS = async (
  task: ApiSchemaPatientTask,
  isDefaultForDHHS: boolean
) => {
  await UpdatePatientTask({ ...task, isDefaultForDHHS });
};

export const ApiPatientTask = {
  GetAll: AccessObject.GetAll,
  Paginated: AccessObject.Paginated,
  ById: AccessObject.ById,
  Add: AddPatientTask,
  Delete: DeletePatientTaskMultiple,
  Update: UpdatePatientTask,
  setIsDefaultForDHHS,
};
